import { FormInstance } from "antd";
import {
  CreditorPayFormat,
  defaultStrategyValue,
  InitialPaymentPercentageOf,
  InterestCalculationStrategy,
  PaymentType,
  ResolutionType,
  StrategyConfigV2,
  StrategyFormat,
  TargetWeeklyCalculationStrategy,
} from "dtos/strategy.dto";

/**
 * What the strategy is for. "lawsuit" only exists in the wizard: it is sent to the API as a negotiate
 * strategy, with lawsuit friendly defaults.
 */
export type StrategyPurpose = "settlement" | "negotiate" | "lawsuit";

/** Shape held by the wizard form. `slug` is absent for strategies that were never saved. */
export type StrategyFormValues = Omit<StrategyConfigV2, "slug"> & { slug?: string; purpose?: StrategyPurpose };

type PurposePreset = {
  resolution_type: ResolutionType;
  initial_fee_base: InitialPaymentPercentageOf;
  is_bias_exclusive: boolean;
  interest_calculation_strategy: InterestCalculationStrategy;
  pay_format: CreditorPayFormat;
  /** Fixed payback ratio; settlement leaves it to the user. */
  total_payback?: number;
};

export const PURPOSE_PRESETS: Record<StrategyPurpose, PurposePreset> = {
  settlement: {
    resolution_type: ResolutionType.SETTLEMENT,
    initial_fee_base: InitialPaymentPercentageOf.TOTAL_DEBT,
    is_bias_exclusive: false,
    interest_calculation_strategy: InterestCalculationStrategy.PERCENTAGE,
    pay_format: CreditorPayFormat.FCDS,
  },
  negotiate: {
    resolution_type: ResolutionType.NEGOTIATE,
    initial_fee_base: InitialPaymentPercentageOf.TOTAL_DEBT,
    is_bias_exclusive: false,
    interest_calculation_strategy: InterestCalculationStrategy.PERCENTAGE,
    pay_format: CreditorPayFormat.FCDS,
    total_payback: 1,
  },
  lawsuit: {
    resolution_type: ResolutionType.NEGOTIATE,
    initial_fee_base: InitialPaymentPercentageOf.TOTAL_INTEREST,
    is_bias_exclusive: true,
    interest_calculation_strategy: InterestCalculationStrategy.FIXED_PER_LAWSUIT,
    pay_format: CreditorPayFormat.DIRECT,
    total_payback: 1,
  },
};

/** Lawsuit plans are the only negotiate plans that pay creditors directly. */
export const derivePurpose = (strategy?: Partial<StrategyConfigV2>): StrategyPurpose => {
  if (strategy?.resolution_type === ResolutionType.SETTLEMENT) return "settlement";
  if (strategy?.creditor_split?.pay_format === CreditorPayFormat.DIRECT) return "lawsuit";
  return "negotiate";
};

const withPurposePreset = (values: StrategyFormValues, purpose: StrategyPurpose): StrategyFormValues => {
  const { total_payback, pay_format, ...preset } = PURPOSE_PRESETS[purpose];
  return {
    ...values,
    ...preset,
    purpose,
    creditor_split: {
      ...values.creditor_split,
      pay_format,
      total_payback: total_payback ?? values.creditor_split?.total_payback ?? 1,
    },
  };
};

/** Applies a purpose's defaults to the wizard form (used when the user picks a purpose). */
export const applyPurposePreset = (form: FormInstance<StrategyFormValues>, purpose: StrategyPurpose) => {
  const next = withPurposePreset(form.getFieldsValue(true), purpose);
  form.setFieldsValue({
    purpose,
    resolution_type: next.resolution_type,
    initial_fee_base: next.initial_fee_base,
    is_bias_exclusive: next.is_bias_exclusive,
    interest_calculation_strategy: next.interest_calculation_strategy,
    creditor_split: next.creditor_split,
  });
};

export const MONTHLY_WEEK_CALCULATIONS = [
  TargetWeeklyCalculationStrategy.PRIOR_BIAS,
  TargetWeeklyCalculationStrategy.COMPACT,
];

export const WIZARD_INTEREST_STRATEGIES = [
  InterestCalculationStrategy.PERCENTAGE,
  InterestCalculationStrategy.FIXED_PER_LAWSUIT,
];

const withProgramSplitDefaults = (program_split?: StrategyConfigV2["program_split"]) => {
  const defaults = defaultStrategyValue.program_split;
  return {
    ...defaults,
    ...program_split,
    meta: {
      ...defaults.meta,
      ...program_split?.meta,
      steps: program_split?.meta?.steps?.length ? program_split.meta.steps : defaults.meta.steps,
    },
  };
};

/** Normalises a saved (or org level) strategy into the values the wizard edits. */
export const toWizardValues = (strategy?: Partial<StrategyConfigV2>): StrategyFormValues => {
  const defaults: Partial<typeof defaultStrategyValue> = { ...defaultStrategyValue };
  delete defaults.dynamic_pushback_toggle;

  return {
    ...(defaults as Omit<typeof defaultStrategyValue, "dynamic_pushback_toggle">),
    ...strategy,
    format: StrategyFormat.MANAGERS_APPROVAL,
    target_weekly_calculation_strategy:
      strategy?.target_weekly_calculation_strategy ?? TargetWeeklyCalculationStrategy.PRIOR_BIAS,
    interest_calculation_strategy: strategy?.interest_calculation_strategy ?? InterestCalculationStrategy.PERCENTAGE,
    program_split: withProgramSplitDefaults(strategy?.program_split),
    creditor_split: { ...defaultStrategyValue.creditor_split, ...strategy?.creditor_split },
    meta: { ...strategy?.meta },
    purpose: derivePurpose(strategy),
  };
};

/** Values for a brand new custom strategy, optionally copied from an organisation plan. */
export const buildBaseStrategy = (basePlan: StrategyConfigV2 | undefined, title: string): StrategyFormValues => {
  const values = toWizardValues(basePlan);
  return withPurposePreset({ ...values, slug: undefined, key: title, description: "" }, values.purpose ?? "negotiate");
};

/** Strips wizard only fields so the payload matches what the API expects. */
export const toStrategyConfig = (values: StrategyFormValues): StrategyConfigV2 => {
  const config = { ...values, format: StrategyFormat.MANAGERS_APPROVAL } as StrategyConfigV2 & {
    dynamic_pushback_toggle?: boolean;
    purpose?: StrategyPurpose;
    net_commission?: number;
    initial_payment?: number;
  };
  if (config.purpose) config.resolution_type = PURPOSE_PRESETS[config.purpose].resolution_type;
  delete config.dynamic_pushback_toggle;
  delete config.purpose;
  // Written by the old editor (advanced mode); not part of the V2 config.
  delete config.net_commission;
  delete config.initial_payment;

  if (
    config.payment_type === PaymentType.MONTHLY &&
    !MONTHLY_WEEK_CALCULATIONS.includes(config.target_weekly_calculation_strategy)
  ) {
    config.target_weekly_calculation_strategy = TargetWeeklyCalculationStrategy.PRIOR_BIAS;
  }

  return config;
};

/** A strategy counts as selected by the customer when its slug (or legacy index key) matches. */
export const isSelectedStrategy = (strategy: StrategyConfigV2 | undefined, index: number, selected?: string) =>
  Boolean(selected) && [strategy?.slug, `custom-plan-${index + 1}`].includes(selected);
